import axios from 'axios';
import { useQuery, useQueryClient } from 'react-query';

const apiUrl = (): string => process.env.REACT_APP_BASIX_API || '';

export interface CreditMemoRow {
  id: string;
  number: string;
  customerId: string;
  customerName: string;
  projectId?: string | null;
  projectName?: string | null;
  amount: number;
  remaining: number;
  note: string;
  sourceInvoiceId?: string | null;
  createdDate: string;
}

export interface CreditMemoApplicationRow {
  id: string;
  creditMemoId: string;
  invoiceId: string;
  amount: number;
  createdDate: string;
  appliedAfterFinalize?: boolean; // item 1575 — applied to an already-finalized invoice (can be un-applied)
}

// item 1221 (Chad) — customer credit memos (overpayments): the ledger feed, the manual add
// (Pulse-admin), and void. Application to a DRAFT happens inside invoice finalize (atomic) —
// the finalize modal passes applyCreditMemoId on the finalize call itself. item 1575 — a credit
// can also be applied to (and un-applied from) an already-finalized, unpaid invoice.
const useCreditMemos = () => {
  const qc = useQueryClient();
  const { data, status } = useQuery('credit-memos', async () => {
    const r = await axios.get(`${apiUrl()}CreditMemo`);
    return r.data as { index: CreditMemoRow[]; applications: CreditMemoApplicationRow[] };
  }, { staleTime: 30_000, retry: false });

  const refresh = () => qc.invalidateQueries('credit-memos');

  const create = async (input: { customerId: string; projectId?: string | null; amount: number; note: string }) => {
    const r = await axios.post(`${apiUrl()}CreditMemo`, input);
    refresh();
    return r.data as { id: string; number: string };
  };

  const voidMemo = async (id: string) => {
    await axios.post(`${apiUrl()}CreditMemo/${id}/void`, {});
    refresh();
  };

  const applyToInvoice = async (input: { creditMemoId: string; invoiceId: string; amount?: number | null }) => {
    const r = await axios.post(`${apiUrl()}CreditMemo/apply-to-invoice`, input);
    refresh();
    return r.data as { id: string; applied: number; balanceDue: number; settled: boolean; memoNumber: string; memoRemaining: number };
  };

  const unapply = async (applicationId: string) => {
    await axios.post(`${apiUrl()}CreditMemo/application/${applicationId}/reverse`, {});
    refresh();
  };

  return {
    memos: data?.index ?? [],
    applications: data?.applications ?? [],
    status,
    create,
    voidMemo,
    applyToInvoice,
    unapply,
    refresh,
  };
};

export default useCreditMemos;
